/**
 * useEnrollments — fetch the current user's course enrollments and catalog.
 *
 * EPIC-08-04 AC-2: sorted in-progress first, not-started second, completed last.
 * EPIC-12-05: includes track, min_level, due_date, is_mandatory for level filtering.
 */

import { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import type { CompetencyTrack } from '../types';

export type EnrollmentStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'overdue';

export type ModuleType = 'roleplay' | 'quiz' | 'reading' | 'video';

export interface Enrollment {
  id: string;
  course_id: string;
  course_title: string;
  course_description: string;
  module_type: ModuleType;
  language: string;
  tags: string[];
  status: EnrollmentStatus;
  score: number | null;
  modules_completed: number;
  modules_total: number;
  due_date: string | null;
  allow_self_enroll: boolean;
  assigned_by?: string | null;
  track: CompetencyTrack | null;
  min_level: string | null;
  is_mandatory: boolean;
  banner_image?: string | null;
  duration_minutes?: number | null;
  average_rating?: number | null;
  rating_count?: number;
}

export interface CatalogCourse {
  id: string;
  title: string;
  description: string;
  module_type: ModuleType;
  language: string;
  tags: string[];
  allow_self_enroll: boolean;
  assigned_by?: string | null;
  enrollment_id?: string | null;
  status?: EnrollmentStatus | null;
  track: CompetencyTrack | null;
  min_level: string | null;
  max_level: string | null;
  banner_image?: string | null;
  duration_minutes?: number | null;
  average_rating?: number | null;
  rating_count?: number;
}

const STATUS_SORT_ORDER: Record<EnrollmentStatus, number> = {
  overdue: 0,
  in_progress: 1,
  not_started: 2,
  failed: 3,
  completed: 4,
};

/** Return true if the enrollment's due_date has passed. */
function isOverdue(enrollment: Enrollment): boolean {
  if (!enrollment.due_date) return false;
  if (enrollment.status === 'completed') return false;
  return new Date(enrollment.due_date) < new Date();
}

export function useEnrollments() {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [catalog, setCatalog] = useState<CatalogCourse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [enrollResult, catalogResult] = await Promise.allSettled([
        apiClient.get<{ data: { enrollments: Enrollment[] } }>('/enrollments'),
        apiClient.get<{ data: CatalogCourse[] }>('/courses?catalog=true'),
      ]);

      // Resolve enrollments (fall back to empty array on error)
      const rawEnrollments =
        enrollResult.status === 'fulfilled'
          ? enrollResult.value.data.data.enrollments
          : [];

      const enrolled = rawEnrollments.map((e) => ({
        ...e,
        track: e.track ?? null,
        min_level: e.min_level ?? null,
        is_mandatory: e.is_mandatory ?? false,
        status: isOverdue(e) ? ('overdue' as EnrollmentStatus) : e.status,
      }));

      const sorted = [...enrolled].sort(
        (a, b) => STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status],
      );
      setEnrollments(sorted);

      // Resolve catalog (fall back to empty array on error)
      const rawCatalog =
        catalogResult.status === 'fulfilled'
          ? (catalogResult.value.data.data as CatalogCourse[])
          : [];

      const enrolledIds = new Set(enrolled.map((e) => e.course_id));
      setCatalog(
        rawCatalog
          .filter((c) => !enrolledIds.has(c.id))
          .map((c) => ({
            ...c,
            track: c.track ?? null,
            min_level: c.min_level ?? null,
            max_level: c.max_level ?? null,
          })),
      );

      // Only surface an error if BOTH calls failed
      if (enrollResult.status === 'rejected' && catalogResult.status === 'rejected') {
        setError('load_failed');
      }
    } catch {
      setError('load_failed');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const enroll = useCallback(async (courseId: string): Promise<void> => {
    await apiClient.post('/enrollments', { course_id: courseId });
    await load();
  }, [load]);

  return { enrollments, catalog, isLoading, error, reload: load, enroll };
}
